<script setup>
import {computed} from "vue";

const props = defineProps({
  status: {
    required: true,
    type: Number,
  },
  addedViaSitemap: {
    required: false,
    type: Boolean,
    default: false,
  },
});

const statusConfig = {
  200: {
    text: "The page was successfully retrieved",
    color: "tw-bg-actions-technical",
  },
  204: {
    text: "We found the page and could crawl it but there was no content",
    color: "tw-bg-actions-technical",
  },
  301: {
    text: "The page has been permanently redirected",
    color: "tw-bg-actions-connections",
  },
  302: {
    text: "The page has been temporarily redirected",
    color: "tw-bg-actions-connections",
  },
  304: {
    text: "The page has been found but not modified so we use a cached version",
    color: "tw-bg-actions-connections",
  },
  400: {
    text: "We could not retrieve the page as the server could not understand the request",
    color: "tw-bg-actions-keyword",
  },
  401: {
    text: "We could not crawl the page as we were not authorised to view it",
    color: "tw-bg-actions-keyword",
  },
  402: {
    text: "We could not crawl the page as a payment was required to view it",
    color: "tw-bg-actions-keyword",
  },
  403: {
    text: "We could not crawl the page as we were not authorised to view it",
    color: "tw-bg-actions-keyword",
  },
  404: {
    text: "The page in question could not be found so we could not crawl it",
    color: "tw-bg-actions-keyword",
  },
  406: {
    text: "We crawled the page but the response was not acceptable",
    color: "tw-bg-actions-keyword",
  },
  408: {
    text: "We tried to crawl the page but it would not load within 20 seconds",
    color: "tw-bg-actions-keyword",
  },
  409: {
    text: "We tried to crawl the page but the request conflicts with the current state of the server.",
    color: "tw-bg-actions-keyword",
  },
  410: {
    text: "We tried to crawl the page but the page had been permanently removed from the website",
    color: "tw-bg-actions-keyword",
  },
  429: {
    text: "We tried to crawl the page but were blocked as there were too many requests being made at the same time.",
    color: "tw-bg-actions-keyword",
  },
  500: {
    text: "We tried to crawl the page but the server has encountered a situation it doesn't know how to handle.",
    color: "tw-bg-actions-onsite",
  },
  502: {
    text: "We tried to crawl the page but the server, while working as a gateway to get a response needed to handle the request, got an invalid response.",
    color: "tw-bg-actions-onsite",
  },
  503: {
    text: "We tried to crawl the page but the server is down for maintenance or is overloaded",
    color: "tw-bg-actions-onsite",
  },
  504: {
    text: "We tried to crawl the page but it would not load within 20 seconds",
    color: "tw-bg-actions-onsite",
  },
  507: {
    text: "We tried to crawl the page but the server is unable to store the representation needed to successfully complete the request.",
    color: "tw-bg-actions-onsite",
  },
  508: {
    text: "We tried to crawl the page but we detected an infinite loop while processing the request.",
    color: "tw-bg-actions-onsite",
  },
  520: {
    text: "We tried to crawl the page but an error occurred",
    color: "tw-bg-actions-onsite",
  },
  600: {
    text: "[Platform Specific Code] We tried to crawl the page but an error occurred",
    color: "tw-bg-blue",
  },
  601: {
    text: "[Platform Specific Code] When we started to crawl your homepage we noticed that the canonical link on the page did not match the page url so we did not crawl it.",
    color: "tw-bg-blue",
  },
  603: {
    text: "The page is a brand new page that hasn't been created yet.",
    color: "tw-bg-blue",
  },
};

const tooltipText = computed(() => {
  if (props.addedViaSitemap) {
    return "The page was added via the sitemap, we didn't find it when crawling the website.";
  }
  return statusConfig[props.status]?.text || 'Unknown status';
});

const backgroundColour = computed(() => {
  if (props.addedViaSitemap) {
    return 'tw-bg-blue';
  }
  return statusConfig[props.status]?.color || 'tw-bg-blue';
});
</script>

<template>
  <div
    v-tooltip="{
      content: `${tooltipText}`,
      placement: 'bottom',
      theme: 'dark-blue-tooltip',
    }"
    class="tw-rounded-xl tw-w-10 tw-h-10 tw-py-1 tw-font-semibold tw-text-sm tw-text-white tw-flex tw-justify-center tw-items-center"
    :class="backgroundColour"
  >
    <span class="tw-truncate tw-w-auto tw-block">
      <svg-vue
        v-if="addedViaSitemap"
        icon="icons/sitemap"
        class="tw-w-6 tw-h-6 tw-flex-shrink-0 tw-text-white"
      />
      <template v-else>{{ status }}</template>
    </span>
  </div>
</template>
